/**
 * The VR interface: what a headset shows in place of the page UI, which can't show inside an
 * immersive session.
 *
 * - **Wrist menu** on the left controller (or the left hand): it appears when the visitor looks at
 *   their wrist, or stays up after a press of the left thumbstick. It lists the menus the scene
 *   offers (Waypoints, the visitor directory, Layers, Measurements, Scenes, links saved for later),
 *   Sound and Exit VR, with the scene's label overrides.
 * - **A floating panel** for the menu picked there, about 85 cm in front of the visitor: driven by
 *   the same state as the page's menus (vrMenus.ts), so ticking a layer, a folder or a measurement
 *   set changes the scene as the page does and both stay in sync. Lists scroll with the stick of
 *   the hand pointing at them, or by dragging; the header drags the panel; × closes it.
 * - **Popups** (hotspots, models, scene-menu media) on their own panel: text, a photo gallery, a
 *   video on its own quad, PDF pages (pdf.js), and "Open after VR" for links and web pages, which
 *   the page lists once the visitor leaves VR (vrSavedLinks.ts).
 * - **Scene overlays** on a panel to the visitor's left; 'top' overlays and a tour stop's name on a
 *   banner that floats above the view.
 * - **Measurements** drawn in the scene (lines and value labels), as the page's overlay draws them.
 *
 * A controller or hand ray pointed at a panel wins over teleporting (beginSelect / endSelect from
 * createViewer's select handling) and over flying with that hand's stick (stickCaptured).
 * Panels are canvas textures (vrPanelKit.ts explains why) in their own layer on top of the scene.
 */
import * as pc from 'playcanvas';
import { VR_DRAW_ORDER, VrPanel, vrUiLayer, type PanelRayHit } from './vrPanel';
import { VrPopupPanel } from './vrPopupPanel';
import {
    VR_BANNER_METRES, VR_BANNER_PIXELS, VR_LABEL_PIXELS, VR_WRIST_METRICS,
    applyVrKey, layoutVrKeyboard, layoutVrListPanel, paintVrBanner, paintVrKeyboard, paintVrLabel, paintVrListPanel, panelHitAt, vrPanelTheme,
    type HitRegion, type VrButton, type VrListPanelLayout, type VrListPanelSpec, type VrPanelTheme,
} from './vrPanelKit';
import {
    addSavedLink, directoryRows, layerRows, measurementRows, savedLinkRows, sceneRows, vrWristEntries, waypointRows, wristRows,
    type VrLabels, type VrMenuId, type VrPopupSource, type VrSavedLink, type VrSceneMenuItem, type VrStopSource,
} from './vrMenus';
import type { DirectoryView, VisitorDirectoryState } from '../visitorDirectory';
import type { LayersMenuSource } from '../regionsRuntime';
import type { MeasurementSetsState } from '../../utils/measurementSets';
import type { OverlayData } from '../../types';

/** Saved measurements to draw in VR, in world space (createViewer's measurement tool supplies them). */
export interface VrMeasurementGeometry {
    color: string;
    segments: ReadonlyArray<{ from: pc.Vec3; to: pc.Vec3; label: string }>;
    areas: ReadonlyArray<{ points: readonly pc.Vec3[]; center: pc.Vec3; label: string }>;
}

/** What the VR interface reads and does: createViewer implements it over the viewer's own state. */
export interface VrInterfaceHost {
    labels(): VrLabels;
    accent(): string;
    /** The waypoints menu (null: no tour stops nor extra waypoints in it). */
    waypoints(): { stops: readonly VrStopSource[]; extras: ReadonlyArray<{ id: string; name: string }>; current: number } | null;
    goToStop(index: number): void;
    stepStop(step: 1 | -1): void;
    goToExtraWaypoint(id: string): void;
    /** The visitor directory when it shows (on, and listing something). */
    directory(): VisitorDirectoryState | null;
    activateHotspot(id: string): void;
    goToDirectoryView(view: DirectoryView): void;
    isHotspotAvailable(id: string): boolean;
    /** The Layers menu (null: no region in it). */
    layers(): LayersMenuSource | null;
    /** Saved measurements visitors can see (null: none in this scene). */
    measurements(): { sets: MeasurementSetsState | null; shown: boolean; geometry: () => VrMeasurementGeometry | null } | null;
    setMeasurementsShown(shown: boolean): void;
    sceneMenu(): readonly VrSceneMenuItem[];
    openSceneItem(item: VrSceneMenuItem): void;
    /** Null: nothing to mute. */
    muted(): boolean | null;
    toggleMute(): void;
    exitVr(): void;
    dismissOverlay(id: string): void;
    /** Load pdf.js (the viewer's own loader). */
    loadPdfJs(): Promise<any>;
    requestRender(reason: string): void;
    /** The popup panel closed (its ×, or a select at nothing). */
    onPopupClosed(): void;
}


const MENU_METRES = { width: 0.62, height: 0.78 } as const;
const MENU_PIXELS = { width: 1024, height: 1288 } as const;
const KEYBOARD_METRES = { width: 0.62, height: 0.24 } as const;
const KEYBOARD_PIXELS = { width: 1024, height: 396 } as const;
const WRIST_METRES = { width: 0.15, height: 0.225 } as const;
const WRIST_PIXELS = { width: 512, height: 768 } as const;
const LABEL_METRES = { width: 0.16, height: 0.04 } as const;
/** How fast a fully pushed stick scrolls a list, in canvas pixels per second. */
const STICK_SCROLL = 1400;
/** A press that moves less than this (canvas pixels) is a click, not a drag-scroll. */
const CLICK_SLOP = 18;
/** The wrist menu shows when the head looks within this angle of the left wrist (hides past HIDE). */
const WRIST_SHOW_DEGREES = 26;
const WRIST_HIDE_DEGREES = 40;
/** xr-standard: the thumbstick press. */
const BUTTON_STICK = 3;

type PanelKind = 'wrist' | 'menu' | 'keyboard' | 'popup' | 'overlay' | 'banner' | 'card';

interface Hover {
    panel: VrPanel | null;
    region: string | null;
    hit: PanelRayHit;
}

interface Press {
    panel: VrPanel;
    kind: PanelKind;
    region: string;
    startY: number;
    startScroll: number;
    moved: boolean;
    /** Header drag: the hit's distance along the ray. */
    dragDistance?: number;
}

const _point = new pc.Vec3();
const _head = new pc.Vec3();
const _toWrist = new pc.Vec3();
const _rayEnd = new pc.Vec3();
const _lineA = new pc.Vec3();
const _lineB = new pc.Vec3();
const POINTER = new pc.Color(1, 1, 1, 0.9);
const _hit: PanelRayHit = { x: 0, y: 0, distance: 0 };
const NO_REGIONS: readonly HitRegion[] = [];

export class VrInterface {
    private readonly wrist: VrPanel;
    private readonly menu: VrPanel;
    private readonly keyboard: VrPanel;
    private readonly popup: VrPopupPanel;
    private readonly overlay: VrPopupPanel;
    private readonly banner: VrPanel;
    private readonly labelPanels: VrPanel[] = [];
    private readonly cards: Array<{ panel: VrPanel; url: string | null; title: string }> = [];

    private theme: VrPanelTheme;
    private running = false;
    /** The floating panel's menu. */
    private openMenu: VrMenuId | null = null;
    private menuScroll = 0;
    private menuLayout: VrListPanelLayout | null = null;
    private menuSpec: VrListPanelSpec | null = null;
    private menuDirty = false;
    private wristDirty = true;
    private wristLayout: VrListPanelLayout | null = null;
    private keyboardRegions: HitRegion[] = [];
    private keyboardDirty = false;
    private wristPinned = false;
    private wristLooking = false;
    private wristStickHeld = false;
    private readonly expandedFolders = new Set<string>();
    private searchQuery = '';
    private searchOpen = false;
    private bannerTimer = 0;
    private bannerTopOverlayId: string | null = null;
    private readonly hovers = new Map<pc.XrInputSource, Hover>();
    private readonly presses = new Map<pc.XrInputSource, Press>();
    private readonly captured = { left: false, right: false };
    private savedLinks: VrSavedLink[] = [];
    /** The panels a ray can hit this frame, front-most last (refilled each frame, no allocation). */
    private readonly candidates: Array<{ panel: VrPanel; kind: PanelKind }> = [];
    private cardEntries: Array<{ panel: VrPanel; kind: PanelKind }> = [];
    private readonly menuEntry: { panel: VrPanel; kind: PanelKind };
    private readonly keyboardEntry: { panel: VrPanel; kind: PanelKind };
    private readonly overlayEntry: { panel: VrPanel; kind: PanelKind };
    private readonly popupEntry: { panel: VrPanel; kind: PanelKind };
    private readonly wristEntry: { panel: VrPanel; kind: PanelKind };
    /** Card buttons, laid out once. */
    private readonly cardRegions: HitRegion[] = layoutVrListPanel({ title: '', rows: [], footer: [{ id: 'card:save', label: '' }], closable: false }, { width: 1024, height: 512 }).regions.filter((r) => r.id === 'card:save');
    private measureColor = new pc.Color();
    private measureColorKey = '';
    private readonly unsubscribers: Array<() => void> = [];
    private readonly overlays = new Map<string, OverlayData>();
    private destroyed = false;

    constructor(private readonly app: pc.AppBase, private readonly rig: pc.Entity, private readonly head: pc.Entity, private readonly host: VrInterfaceHost) {
        this.theme = vrPanelTheme(host.accent());
        const labels = () => this.host.labels();
        const theme = () => this.theme;
        const saved = (url: string) => this.savedLinks.some((l) => l.url === url);
        this.wrist = new VrPanel(app, 'vr-wrist-menu', WRIST_METRES, WRIST_PIXELS, app.root, VR_DRAW_ORDER.wrist);
        this.menu = new VrPanel(app, 'vr-menu-panel', MENU_METRES, MENU_PIXELS, rig, VR_DRAW_ORDER.menu);
        this.keyboard = new VrPanel(app, 'vr-keyboard', KEYBOARD_METRES, KEYBOARD_PIXELS, rig, VR_DRAW_ORDER.keyboard);
        // The popup keeps the old info panel's name: scripts and tests find it by it.
        this.popup = new VrPopupPanel(app, 'arContentPlane', rig, host, theme, labels, saved, VR_DRAW_ORDER.popup);
        this.overlay = new VrPopupPanel(app, 'vr-overlay-panel', rig, host, theme, labels, saved, VR_DRAW_ORDER.overlay);
        this.banner = new VrPanel(app, 'vr-banner', VR_BANNER_METRES, VR_BANNER_PIXELS, app.root, VR_DRAW_ORDER.banner);
        this.menuEntry = { panel: this.menu, kind: 'menu' };
        this.keyboardEntry = { panel: this.keyboard, kind: 'keyboard' };
        this.overlayEntry = { panel: this.overlay.panel, kind: 'overlay' };
        this.popupEntry = { panel: this.popup.panel, kind: 'popup' };
        this.wristEntry = { panel: this.wrist, kind: 'wrist' };
    }

    /** A VR session started: draw the UI layer, subscribe to the menus' state. */
    start(): void {
        if (this.destroyed) return;
        this.running = true;
        this.theme = vrPanelTheme(this.host.accent());
        const camera = this.head.camera;
        const layer = vrUiLayer(this.app);
        if (camera && !camera.layers.includes(layer.id)) camera.layers = [...camera.layers, layer.id];
        const layers = this.host.layers();
        if (layers) this.unsubscribers.push(layers.onChange(() => this.onStateChange('layers')));
        const directory = this.host.directory();
        if (directory) this.unsubscribers.push(directory.onChange(() => this.onStateChange('directory')));
        const sets = this.host.measurements()?.sets;
        if (sets) this.unsubscribers.push(sets.onChange(() => this.onStateChange('measurements')));
        this.wristDirty = true;
    }

    /** The session ended: hide everything (links saved for later stay). */
    end(): void {
        this.running = false;
        for (const off of this.unsubscribers.splice(0)) off();
        this.closeMenu();
        this.popup.hide();
        this.overlay.hide();
        this.banner.visible = false;
        this.wrist.visible = false;
        for (const p of this.labelPanels) p.visible = false;
        for (const c of this.cards) c.panel.visible = false;
        this.hovers.clear();
        this.presses.clear();
        this.captured.left = this.captured.right = false;
        this.wristPinned = false;
        this.overlays.clear();
    }

    /** Save a link to open after VR (a scene-menu link, a web page). */
    saveLink(link: VrSavedLink): void {
        this.savedLinks = addSavedLink(this.savedLinks, link);
        this.wristDirty = true;
    }

    /** Links the visitor saved in VR (the page lists them after the session). */
    takeSavedLinks(): VrSavedLink[] {
        const links = this.savedLinks;
        this.savedLinks = [];
        return links;
    }

    get popupVisible(): boolean {
        return this.popup.visible;
    }

    /** Labels or the UI colour changed (setButtonLabels): redraw. */
    refresh(): void {
        this.theme = vrPanelTheme(this.host.accent());
        this.wristDirty = this.menuDirty = this.keyboardDirty = true;
        this.popup.dirty = this.overlay.dirty = true;
    }

    // ── Popups, overlays, banner ───────────────────────────────────────────────────────────────

    showPopup(source: VrPopupSource): void {
        if (!this.running) return;
        this.popup.show(source);
        // Beside an open menu (picking from it shows the popup without covering the list).
        this.popup.panel.placeInFront(this.head, 0.95, 0.05, this.menu.visible ? -0.42 : 0);
        if (this.menu.visible) this.menu.placeInFront(this.head, 0.85, 0.12, 0.38);
        this.syncKeyboardPose();
    }

    hidePopup(): void {
        this.popup.hide();
    }

    showOverlay(overlay: OverlayData): void {
        if (!this.running) return;
        this.overlays.set(overlay.id, overlay);
        if (overlay.layout === 'top') {
            this.bannerTopOverlayId = overlay.id;
            this.showBannerNow(overlay.title ?? '', overlay.subtitle ?? '', 0);
            return;
        }
        this.overlay.show(overlay as VrPopupSource);
        this.overlay.overlayId = overlay.id;
        this.overlay.overlayDismissible = overlay.dismissible !== false;
        // To the left of the view, angled toward the visitor, so it never blocks the scene's centre.
        this.overlay.panel.placeInFront(this.head, 1.05, 0.0, 0.62);
    }

    hideOverlay(id: string): void {
        this.overlays.delete(id);
        if (this.bannerTopOverlayId === id) {
            this.bannerTopOverlayId = null;
            this.banner.visible = false;
        }
        if (this.overlay.overlayId === id) {
            this.overlay.hide();
            // The next most recent overlay still up takes the panel.
            const rest = [...this.overlays.values()].filter((o) => o.layout !== 'top');
            if (rest.length) this.showOverlay(rest[rest.length - 1]);
        }
    }

    /** A tour stop's name (and info) on the banner for a few seconds; top overlays keep it. */
    showBanner(title: string, subtitle: string, seconds = 4): void {
        if (!this.running || this.bannerTopOverlayId) return;
        if (!title && !subtitle) {
            this.banner.visible = false;
            return;
        }
        this.showBannerNow(title, subtitle, seconds);
    }

    private showBannerNow(title: string, subtitle: string, seconds: number): void {
        const ctx = this.banner.ctx;
        if (!ctx) return;
        paintVrBanner(ctx, title, subtitle, this.theme);
        this.banner.commit();
        this.bannerTimer = seconds;
        if (!this.banner.visible) {
            this.banner.visible = true;
            this.placeBanner(1);
        }
        this.host.requestRender('vr-banner');
    }

    /** HTML meshes: page content a headset can't show. A card in their place says so (and saves a link). */
    setHtmlMeshCards(cards: ReadonlyArray<{ position: pc.Vec3; rotation: pc.Quat; url: string | null; title: string }>): void {
        for (const c of this.cards) c.panel.destroy();
        this.cards.length = 0;
        const labels = this.host.labels();
        for (const card of cards) {
            const panel = new VrPanel(this.app, 'vr-html-card', { width: 0.6, height: 0.3 }, { width: 1024, height: 512 }, this.app.root, VR_DRAW_ORDER.label);
            panel.entity.setPosition(card.position);
            panel.entity.setRotation(card.rotation);
            // The card's front (+Z) toward the visitor, whichever way the mesh faces.
            _toWrist.copy(this.head.getPosition()).sub(card.position);
            if (_toWrist.dot(panel.entity.forward) > 0) panel.entity.rotateLocal(0, 180, 0);
            const spec: VrListPanelSpec = {
                title: card.title || labels.webContentOutsideVr,
                rows: [{ id: 'card:info', label: labels.webContentOutsideVr, disabled: true }],
                footer: card.url ? [{ id: 'card:save', label: labels.saveLink, primary: true }] : undefined,
                closable: false,
            };
            const layout = layoutVrListPanel(spec, { width: 1024, height: 512 });
            if (panel.ctx) {
                paintVrListPanel(panel.ctx, layout, spec, this.theme, null);
                panel.commit();
            }
            panel.visible = this.running;
            this.cards.push({ panel, url: card.url, title: card.title });
        }
        this.cardEntries = this.cards.map((c) => ({ panel: c.panel, kind: 'card' as PanelKind }));
    }

    // ── Input ──────────────────────────────────────────────────────────────────────────────────

    /** Whether the stick of this hand scrolls a panel this frame (the visitor doesn't fly with it). */
    stickCaptured(handedness: string): boolean {
        return handedness === 'left' ? this.captured.left : handedness === 'right' ? this.captured.right : false;
    }

    /** Refill the panels a ray can hit, front-most draw order last. */
    private refreshCandidates(): void {
        const list = this.candidates;
        list.length = 0;
        for (const c of this.cardEntries) if (c.panel.visible) list.push(c);
        if (this.menu.visible) list.push(this.menuEntry);
        if (this.keyboard.visible) list.push(this.keyboardEntry);
        if (this.overlay.visible) list.push(this.overlayEntry);
        if (this.popup.visible) list.push(this.popupEntry);
        if (this.wrist.visible) list.push(this.wristEntry);
    }

    /** The nearest panel a ray hits (into `out`, with the region under the hit), or null. */
    private castRay(origin: pc.Vec3, dir: pc.Vec3, out: Hover): { panel: VrPanel; kind: PanelKind } | null {
        let best: { panel: VrPanel; kind: PanelKind } | null = null;
        let bestDistance = Infinity;
        for (const entry of this.candidates) {
            if (!entry.panel.hit(origin, dir, _hit)) continue;
            // Closer wins; at the same distance the one drawn later (in front).
            if (_hit.distance <= bestDistance + 0.002) {
                best = entry;
                bestDistance = _hit.distance;
                out.hit.x = _hit.x;
                out.hit.y = _hit.y;
                out.hit.distance = _hit.distance;
            }
        }
        if (!best) {
            out.panel = null;
            out.region = null;
            return null;
        }
        out.panel = best.panel;
        out.region = panelHitAt(this.regionsOf(best.kind), out.hit.x, out.hit.y)?.id ?? null;
        return best;
    }

    private regionsOf(kind: PanelKind): readonly HitRegion[] {
        if (kind === 'menu') return this.menuLayout?.regions ?? NO_REGIONS;
        if (kind === 'wrist') return this.wristLayout?.regions ?? NO_REGIONS;
        if (kind === 'keyboard') return this.keyboardRegions;
        if (kind === 'popup') return this.popup.regions();
        if (kind === 'overlay') return this.overlay.regions();
        // A card without a link has nothing to press.
        if (kind === 'card') return this.cardRegions;
        return NO_REGIONS;
    }

    /** A select (trigger / pinch) started: true when it is on a panel (the UI takes it). */
    beginSelect(source: pc.XrInputSource): boolean {
        if (!this.running) return false;
        const hover: Hover = { panel: null, region: null, hit: { x: 0, y: 0, distance: 0 } };
        this.refreshCandidates();
        const hit = this.castRay(source.getOrigin(), source.getDirection(), hover);
        if (!hit || !hover.panel) return false;
        const press: Press = {
            panel: hover.panel,
            kind: hit.kind,
            region: hover.region ?? '',
            startY: hover.hit.y,
            startScroll: hit.kind === 'menu' ? this.menuScroll : hit.kind === 'popup' ? this.popup.scrollPosition : hit.kind === 'overlay' ? this.overlay.scrollPosition : 0,
            moved: false,
        };
        if (press.region === '__drag') press.dragDistance = hover.hit.distance;
        this.presses.set(source, press);
        return true;
    }

    /** The select ended: a click on what it started on (unless it dragged). */
    endSelect(source: pc.XrInputSource): void {
        const press = this.presses.get(source);
        this.presses.delete(source);
        if (!press || press.moved || press.region === '__drag') return;
        const hover = this.hovers.get(source);
        // Released over another region (or off the panel): nothing, like a click.
        const now = hover?.panel === press.panel ? hover.region : null;
        if (now !== press.region) return;
        this.runAction(press.kind, press.panel, press.region);
    }

    private runAction(kind: PanelKind, panel: VrPanel, id: string): void {
        if (!id || id === '__list' || id === '__text' || id === '__media') return;
        if (kind === 'wrist') return this.runWrist(id);
        if (kind === 'keyboard') {
            this.searchQuery = applyVrKey(this.searchQuery, id);
            this.menuScroll = 0;
            this.menuDirty = true;
            return;
        }
        if (kind === 'popup' || kind === 'overlay') {
            const popup = kind === 'popup' ? this.popup : this.overlay;
            if (id === '__close') {
                if (kind === 'overlay' && popup.overlayId) {
                    const overlayId = popup.overlayId;
                    popup.hide();
                    this.overlays.delete(overlayId);
                    this.host.dismissOverlay(overlayId);
                } else {
                    popup.hide();
                    this.host.onPopupClosed();
                }
                return;
            }
            if (id === 'save-link' && popup.content?.link) {
                this.savedLinks = addSavedLink(this.savedLinks, popup.content.link);
                popup.dirty = true;
                this.wristDirty = true;
                return;
            }
            popup.run(id);
            return;
        }
        if (kind === 'card') {
            const card = this.cards.find((c) => c.panel === panel);
            if (id === 'card:save' && card?.url) {
                this.savedLinks = addSavedLink(this.savedLinks, { url: card.url, label: card.title || card.url });
                this.wristDirty = true;
            }
            return;
        }
        if (kind === 'menu') this.runMenu(id);
    }

    private runWrist(id: string): void {
        const action = id.replace(/^wrist:/, '');
        if (action === 'sound') {
            this.host.toggleMute();
            this.wristDirty = true;
            return;
        }
        if (action === 'exit') {
            this.host.exitVr();
            return;
        }
        const menu = action as VrMenuId;
        // The same entry again closes the panel; another one switches it (and brings it in front).
        if (this.openMenu === menu && this.menu.visible) this.closeMenu();
        else this.showMenu(menu);
    }

    /** Open a menu on the floating panel, in front of the visitor. */
    showMenu(menu: VrMenuId): void {
        if (!this.running) return;
        if (this.openMenu !== menu) {
            this.menuScroll = 0;
            this.searchOpen = false;
            this.searchQuery = '';
        }
        this.openMenu = menu;
        this.menu.visible = true;
        this.menu.placeInFront(this.head, 0.85, 0.12, this.popup.visible ? 0.38 : 0);
        this.menuDirty = true;
        this.wristDirty = true;
        this.keyboard.visible = false;
        this.syncKeyboardPose();
    }

    closeMenu(): void {
        this.openMenu = null;
        this.menu.visible = false;
        this.keyboard.visible = false;
        this.searchOpen = false;
        this.wristDirty = true;
    }

    private runMenu(id: string): void {
        const host = this.host;
        if (id === '__close') return this.closeMenu();
        const [kind, ...rest] = id.split(':');
        const arg = rest.join(':');
        switch (kind) {
            case 'stop':
                host.goToStop(Number(arg));
                break;
            case 'view':
                host.goToExtraWaypoint(arg);
                break;
            case 'prev':
                host.stepStop(-1);
                break;
            case 'next':
                host.stepStop(1);
                break;
            case 'folder':
                if (this.expandedFolders.has(arg)) this.expandedFolders.delete(arg);
                else this.expandedFolders.add(arg);
                break;
            case 'check': {
                const directory = host.directory();
                const state = directory?.folderCheckState(arg);
                if (directory && state) directory.setFolderVisible(arg, state !== 'on');
                break;
            }
            case 'hotspot':
                host.activateHotspot(arg);
                break;
            case 'dview': {
                const view = host.directory()?.getAllViews().find((v) => v.id === arg);
                if (view) host.goToDirectoryView(view);
                break;
            }
            case 'search':
                this.searchOpen = !this.searchOpen;
                this.keyboard.visible = this.searchOpen;
                this.keyboardDirty = true;
                this.syncKeyboardPose();
                break;
            case 'layer-all':
                host.layers()?.selectAll();
                break;
            case 'layer':
                host.layers()?.select(arg);
                break;
            case 'measure': {
                const m = host.measurements();
                if (m) host.setMeasurementsShown(!m.shown);
                break;
            }
            case 'mset-all': {
                const sets = host.measurements()?.sets;
                if (sets) sets.setAllVisible(!sets.allVisible());
                break;
            }
            case 'mset': {
                const sets = host.measurements()?.sets;
                if (sets) sets.setVisible(arg, !sets.isShown(arg));
                break;
            }
            case 'portal':
            case 'link': {
                const item = host.sceneMenu().find((i) => i.kind === kind && i.id === arg);
                if (item) host.openSceneItem(item);
                break;
            }
            default:
                return;
        }
        this.menuDirty = true;
    }

    private onStateChange(menu: VrMenuId): void {
        if (this.openMenu === menu) this.menuDirty = true;
        this.host.requestRender('vr-menu');
    }

    /** The keyboard sits under the menu panel, tilted toward the visitor. */
    private syncKeyboardPose(): void {
        if (!this.keyboard.visible || !this.menu.visible) return;
        this.menu.pointAt(MENU_PIXELS.width / 2, MENU_PIXELS.height, _point);
        const down = this.menu.entity.up;
        this.keyboard.entity.setPosition(_point.x - down.x * (KEYBOARD_METRES.height / 2 + 0.02), _point.y - down.y * (KEYBOARD_METRES.height / 2 + 0.02), _point.z - down.z * (KEYBOARD_METRES.height / 2 + 0.02));
        this.keyboard.entity.setRotation(this.menu.entity.getRotation());
        this.keyboard.entity.rotateLocal(-18, 0, 0);
    }

    // ── Per frame ──────────────────────────────────────────────────────────────────────────────

    update(dt: number): void {
        if (!this.running || this.destroyed) return;
        const xr = this.app.xr;
        const sources = xr?.input.inputSources ?? [];
        this.captured.left = this.captured.right = false;
        this.updateWrist(sources);
        this.refreshCandidates();
        for (const source of sources) this.updateSource(source, dt);
        // Hover per panel: what any ray points at.
        this.syncHover();
        this.updateBanner(dt);
        this.drawDirty();
        this.popup.update();
        this.overlay.update();
        this.drawMeasurements();
    }

    private updateSource(source: pc.XrInputSource, dt: number): void {
        let hover = this.hovers.get(source);
        if (!hover) {
            hover = { panel: null, region: null, hit: { x: 0, y: 0, distance: 0 } };
            this.hovers.set(source, hover);
        }
        if (source.targetRayMode !== pc.XRTARGETRAY_POINTER) {
            hover.panel = null;
            hover.region = null;
            return;
        }
        const origin = source.getOrigin();
        const dir = source.getDirection();
        const press = this.presses.get(source);
        if (press?.region === '__drag' && press.dragDistance !== undefined) {
            // Dragging a panel by its header: it follows the ray, facing the visitor.
            _rayEnd.copy(dir).mulScalar(press.dragDistance).add(origin);
            press.panel.entity.setPosition(_rayEnd);
            press.panel.faceTowards(this.head.getPosition());
            if (press.panel === this.menu) this.syncKeyboardPose();
            hover.panel = press.panel;
            hover.region = '__drag';
            this.drawPointer(origin, _rayEnd);
            return;
        }
        const hit = this.castRay(origin, dir, hover);
        if (press && hover.panel === press.panel) {
            // Drag-scrolling a list or a popup's text.
            const dy = hover.hit.y - press.startY;
            if (Math.abs(dy) > CLICK_SLOP) press.moved = true;
            if (press.moved) {
                if (press.kind === 'menu') {
                    this.menuScroll = press.startScroll - dy;
                    this.menuDirty = true;
                } else if (press.kind === 'popup') this.popup.setScroll(press.startScroll - dy);
                else if (press.kind === 'overlay') this.overlay.setScroll(press.startScroll - dy);
            }
        }
        if (!hit || !hover.panel) return;
        _lineB.copy(dir).mulScalar(hover.hit.distance).add(origin);
        this.drawPointer(origin, _lineB);
        // The stick of the hand pointing at a scrollable panel scrolls it.
        const pad = source.gamepad as { axes: ReadonlyArray<number> } | null;
        const axis = pad && pad.axes.length >= 4 ? pad.axes[3] : 0;
        if (hit.kind === 'menu' || hit.kind === 'popup' || hit.kind === 'overlay') {
            if (source.handedness === 'left') this.captured.left = true;
            else if (source.handedness === 'right') this.captured.right = true;
            if (Math.abs(axis) > 0.15) {
                const by = axis * STICK_SCROLL * dt;
                if (hit.kind === 'menu') {
                    this.menuScroll += by;
                    this.menuDirty = true;
                } else (hit.kind === 'popup' ? this.popup : this.overlay).scroll(by);
            }
        }
    }

    private drawPointer(from: pc.Vec3, to: pc.Vec3): void {
        _lineA.copy(from);
        this.app.drawLine(_lineA, to, POINTER, false, vrUiLayer(this.app));
    }

    private lastHover = new Map<VrPanel, string | null>();
    private nextHover = new Map<VrPanel, string | null>();

    private syncHover(): void {
        const now = this.nextHover;
        now.clear();
        for (const hover of this.hovers.values()) if (hover.panel) now.set(hover.panel, hover.region);
        const changed = (panel: VrPanel) => (now.get(panel) ?? null) !== (this.lastHover.get(panel) ?? null);
        if (changed(this.menu)) this.menuDirty = true;
        if (changed(this.wrist)) this.wristDirty = true;
        if (changed(this.keyboard)) this.keyboardDirty = true;
        if (changed(this.popup.panel)) {
            this.popup.hoverId = now.get(this.popup.panel) ?? null;
            this.popup.dirty = true;
        }
        if (changed(this.overlay.panel)) {
            this.overlay.hoverId = now.get(this.overlay.panel) ?? null;
            this.overlay.dirty = true;
        }
        // Swap the two maps (no allocation per frame).
        this.nextHover = this.lastHover;
        this.lastHover = now;
    }

    private updateWrist(sources: readonly pc.XrInputSource[]): void {
        let left: pc.XrInputSource | null = null;
        for (const s of sources) if (s.handedness === 'left') left = s;
        // The left thumbstick's press pins the wrist menu open (or lets it go).
        const pad = left?.gamepad as { buttons: ReadonlyArray<{ pressed: boolean }> } | null | undefined;
        const stick = !!pad?.buttons[BUTTON_STICK]?.pressed;
        if (stick && !this.wristStickHeld) {
            this.wristPinned = !this.wristPinned;
            this.wristDirty = true;
        }
        this.wristStickHeld = stick;
        const anchor = left ? (left.hand?.wrist?.getPosition() ?? left.getPosition() ?? left.getOrigin()) : null;
        if (!anchor) {
            this.wrist.visible = false;
            return;
        }
        _head.copy(this.head.getPosition());
        _toWrist.copy(anchor).sub(_head);
        const distance = _toWrist.length();
        if (distance > 1e-4) _toWrist.mulScalar(1 / distance);
        const angle = Math.acos(Math.min(1, Math.max(-1, _toWrist.dot(this.head.forward)))) * pc.math.RAD_TO_DEG;
        // Looking at the wrist (within reach) shows it; looking away hides it, with some slack.
        this.wristLooking = distance < 0.8 && (this.wristLooking ? angle < WRIST_HIDE_DEGREES : angle < WRIST_SHOW_DEGREES);
        // Never while pressing on it: a press shouldn't lose its target mid-select.
        let pressingWrist = false;
        for (const p of this.presses.values()) if (p.panel === this.wrist) pressingWrist = true;
        const show = this.wristPinned || this.wristLooking || pressingWrist;
        if (show !== this.wrist.visible) {
            this.wrist.visible = show;
            if (show) this.wristDirty = true;
        }
        if (!show) return;
        // Just above the wrist, toward the head, facing it.
        this.wrist.entity.setPosition(anchor.x - _toWrist.x * 0.05, anchor.y + 0.1, anchor.z - _toWrist.z * 0.05);
        this.wrist.faceTowards(_head);
    }

    private placeBanner(lerp: number): void {
        const eye = this.head.getPosition();
        const forward = this.head.forward;
        const flat = Math.hypot(forward.x, forward.z) || 1;
        // About 15° above the centre of view, 1.2 m away.
        _point.set(eye.x + (forward.x / flat) * 1.2, eye.y + 0.34, eye.z + (forward.z / flat) * 1.2);
        const pos = this.banner.entity.getPosition();
        this.banner.entity.setPosition(pos.x + (_point.x - pos.x) * lerp, pos.y + (_point.y - pos.y) * lerp, pos.z + (_point.z - pos.z) * lerp);
        this.banner.faceTowards(eye);
    }

    private updateBanner(dt: number): void {
        if (!this.banner.visible) return;
        // Follows the view lazily, so it never swims with every small head movement.
        this.placeBanner(Math.min(1, dt * 2.5));
        if (this.bannerTimer > 0 && !this.bannerTopOverlayId) {
            this.bannerTimer -= dt;
            if (this.bannerTimer <= 0) this.banner.visible = false;
        }
        this.host.requestRender('vr-banner');
    }

    // ── Drawing ────────────────────────────────────────────────────────────────────────────────

    private wristAvailability() {
        const host = this.host;
        const wp = host.waypoints();
        const directory = host.directory();
        const layers = host.layers();
        return {
            waypoints: !!wp && (wp.stops.some((s) => s.hideInMenu !== true) || wp.extras.length > 0),
            directory: directory ? directory.title : null,
            layers: layers ? layers.panel.title : null,
            measurements: !!host.measurements(),
            scenes: host.sceneMenu().length > 0,
            muted: host.muted(),
            savedLinks: this.savedLinks.length,
        };
    }

    private hoverOf(panel: VrPanel): string | null {
        return this.lastHover.get(panel) ?? null;
    }

    private drawDirty(): void {
        const labels = this.host.labels();
        if (this.wristDirty && this.wrist.visible && this.wrist.ctx) {
            this.wristDirty = false;
            const entries = vrWristEntries(this.wristAvailability(), labels, this.menu.visible ? this.openMenu : null);
            const spec: VrListPanelSpec = { title: labels.menu, rows: wristRows(entries), closable: false };
            this.wristLayout = layoutVrListPanel(spec, WRIST_PIXELS, VR_WRIST_METRICS);
            paintVrListPanel(this.wrist.ctx, this.wristLayout, spec, this.theme, this.hoverOf(this.wrist));
            this.wrist.commit();
        }
        if (this.menuDirty && this.menu.visible && this.menu.ctx && this.openMenu) {
            this.menuDirty = false;
            const spec = this.menuPanelSpec(this.openMenu, labels);
            if (!spec) {
                this.closeMenu();
            } else {
                spec.scroll = this.menuScroll;
                this.menuSpec = spec;
                this.menuLayout = layoutVrListPanel(spec, MENU_PIXELS);
                this.menuScroll = this.menuLayout.scroll;
                paintVrListPanel(this.menu.ctx, this.menuLayout, spec, this.theme, this.hoverOf(this.menu));
                this.menu.commit();
                this.host.requestRender('vr-menu');
            }
        }
        if (this.keyboardDirty && this.keyboard.visible && this.keyboard.ctx) {
            this.keyboardDirty = false;
            const { width, height } = KEYBOARD_PIXELS;
            const keys = layoutVrKeyboard({ x: 20, y: 20, w: width - 40, h: height - 40 });
            this.keyboardRegions = keys.map(({ id, x, y, w, h }) => ({ id, x, y, w, h }));
            paintVrKeyboard(this.keyboard.ctx, KEYBOARD_PIXELS, keys, this.theme, this.hoverOf(this.keyboard));
            this.keyboard.commit();
        }
        this.popup.draw();
        this.overlay.draw();
    }

    private menuPanelSpec(menu: VrMenuId, labels: VrLabels): VrListPanelSpec | null {
        const host = this.host;
        switch (menu) {
            case 'waypoints': {
                const wp = host.waypoints();
                if (!wp) return null;
                const footer: VrButton[] = wp.stops.length > 1
                    ? [{ id: 'prev', label: `‹ ${labels.previous}` }, { id: 'next', label: `${labels.next} ›`, primary: true }]
                    : [];
                return { title: labels.waypoints, rows: waypointRows(wp.stops, wp.extras, wp.current), footer };
            }
            case 'directory': {
                const directory = host.directory();
                if (!directory) return null;
                const rows = directoryRows(directory, { expanded: this.expandedFolders, query: this.searchQuery, isHotspotAvailable: (id) => host.isHotspotAvailable(id) }, labels);
                const search = { id: 'search', label: this.searchQuery || labels.search, sub: undefined, detail: this.searchOpen ? '⌨ ▾' : '⌨', active: this.searchOpen };
                return { title: directory.title, rows: [search, ...rows], empty: labels.noMatch };
            }
            case 'layers': {
                const layers = host.layers();
                if (!layers) return null;
                return { title: layers.panel.title, rows: layerRows(layers) };
            }
            case 'measurements': {
                const m = host.measurements();
                if (!m) return null;
                return { title: labels.measurements, rows: measurementRows(m.sets, m.shown, labels) };
            }
            case 'scenes': {
                const items = host.sceneMenu();
                if (!items.length) return null;
                return { title: labels.scenes, rows: sceneRows(items) };
            }
            case 'links':
                return { title: labels.savedLinksTitle, rows: savedLinkRows(this.savedLinks) };
            default:
                return null;
        }
    }

    /** Saved measurements in the scene: lines (and outlines) with their values on labels. */
    private drawMeasurements(): void {
        const m = this.host.measurements();
        const geometry = m && m.shown ? m.geometry() : null;
        let used = 0;
        if (geometry) {
            if (geometry.color !== this.measureColorKey) {
                this.measureColor.fromString(geometry.color);
                this.measureColorKey = geometry.color;
            }
            const color = this.measureColor;
            const layer = vrUiLayer(this.app);
            for (const s of geometry.segments) {
                this.app.drawLine(s.from, s.to, color, false, layer);
                _point.copy(s.from).add(s.to).mulScalar(0.5);
                this.placeLabel(used++, _point, s.label, geometry.color);
            }
            for (const a of geometry.areas) {
                for (let i = 0; i < a.points.length; i++) this.app.drawLine(a.points[i], a.points[(i + 1) % a.points.length], color, false, layer);
                this.placeLabel(used++, a.center, a.label, geometry.color);
            }
        }
        for (let i = used; i < this.labelPanels.length; i++) this.labelPanels[i].visible = false;
    }

    private placeLabel(index: number, at: pc.Vec3, text: string, color: string): void {
        let panel = this.labelPanels[index];
        if (!panel) {
            panel = new VrPanel(this.app, 'vr-measure-label', LABEL_METRES, VR_LABEL_PIXELS, this.app.root, VR_DRAW_ORDER.label);
            this.labelPanels[index] = panel;
        }
        const key = `${text}|${color}`;
        if ((panel as VrPanel & { _key?: string })._key !== key && panel.ctx) {
            paintVrLabel(panel.ctx, text, color);
            panel.commit();
            (panel as VrPanel & { _key?: string })._key = key;
        }
        panel.visible = true;
        panel.entity.setPosition(at);
        // Same angular size at any distance (as the page's labels are a fixed size on screen).
        const distance = Math.max(0.3, panel.entity.getPosition().distance(this.head.getPosition()));
        const scale = Math.min(4, distance / 1.2);
        panel.setSize(LABEL_METRES.width * scale, LABEL_METRES.height * scale);
        panel.faceTowards(this.head.getPosition());
    }

    /** For tests and debugging: what the interface shows now. */
    debugState() {
        const rowsOf = (spec: VrListPanelSpec | null) => spec?.rows.map((r) => ({ id: r.id, label: r.label, check: r.check ?? null, active: !!r.active })) ?? [];
        let labels = 0;
        for (const p of this.labelPanels) if (p.visible) labels += 1;
        return {
            wrist: this.wrist.visible ? (this.wristLayout?.rows.map((r) => r.row.id) ?? []) : null,
            wristPinned: this.wristPinned,
            menu: this.menu.visible ? { open: this.openMenu, rows: rowsOf(this.menuSpec), scroll: this.menuScroll, query: this.searchQuery } : null,
            keyboard: this.keyboard.visible,
            popup: this.popup.visible ? this.popup.debug() : null,
            overlay: this.overlay.visible ? this.overlay.debug() : null,
            banner: this.banner.visible,
            measurementLabels: labels,
            cards: this.cards.length,
            savedLinks: this.savedLinks.map((l) => l.url),
        };
    }

    /** For tests: the world point at the centre of a panel's region (null when it isn't on screen). */
    regionPoint(panel: 'wrist' | 'menu' | 'keyboard' | 'popup' | 'overlay' | `card:${number}`, id: string): [number, number, number] | null {
        const card = panel.startsWith('card:') ? this.cards[Number(panel.slice(5))]?.panel : null;
        const target = card ?? (panel === 'wrist' ? this.wrist : panel === 'menu' ? this.menu : panel === 'keyboard' ? this.keyboard : panel === 'popup' ? this.popup.panel : this.overlay.panel);
        if (!target) return null;
        const kind: PanelKind = card ? 'card' : (panel as PanelKind);
        const region = this.regionsOf(kind).find((r) => r.id === id);
        if (!region || !target.visible) return null;
        const p = target.pointAt(region.x + region.w / 2, region.y + region.h / 2, new pc.Vec3());
        return [p.x, p.y, p.z];
    }

    destroy(): void {
        if (this.destroyed) return;
        this.end();
        this.destroyed = true;
        this.wrist.destroy();
        this.menu.destroy();
        this.keyboard.destroy();
        this.popup.destroy();
        this.overlay.destroy();
        this.banner.destroy();
        for (const p of this.labelPanels) p.destroy();
        for (const c of this.cards) c.panel.destroy();
    }
}
